import { RefObject, useEffect, useState, useRef } from "react";
import { createPortal, ThreeEvent } from "@react-three/fiber";
import { Stage, OrbitControls } from "@react-three/drei";
import { Mesh, MeshStandardMaterial, Vector2, Vector3 } from "three";

import { StickerConfig } from "@/lib/sticker";
import { StickerMode } from "@/hooks/sticker/useStickerMode";
import { InteractionMode } from "@/hooks/sticker/useInteractionMode";
import { getModelConfig } from "@/lib/models";
import { calculateStickerSize3D } from "@/lib/sizeCalculator";
import { useCameraFocus } from "@/hooks/sticker/useCameraFocus";
import { useDecal } from "@/hooks/sticker/useDecal";
import { usePaintableTexture } from "@/hooks/sticker/usePaintableTexture";
import { useStickerTextureDrag } from "@/hooks/sticker/useStickerTextureDrag";

import { Model } from "../common/Model";
import { Sticker } from "./Sticker";

interface StickerSceneProps {
  selectedModel: string;
  stickerConfig: StickerConfig;
  mode: StickerMode;
  interactionMode: InteractionMode;
  meshRef: RefObject<Mesh>;
  textureUvPoint: Vector2 | null;
  decalPoint: Vector3 | null;
  decalNormal: Vector3 | null;
  onMeshClick: (event: ThreeEvent<MouseEvent>) => void;
  onTextureUvUpdate: (uvPoint: Vector2) => void;
  onCameraReset?: { current: (() => void) | null };
}

export const StickerScene = ({
  selectedModel,
  stickerConfig,
  mode,
  interactionMode,
  meshRef,
  textureUvPoint,
  decalPoint,
  decalNormal,
  onMeshClick,
  onTextureUvUpdate,
  onCameraReset,
}: StickerSceneProps) => {
  const modelConfig = getModelConfig(selectedModel);
  const decal = useDecal(
    meshRef,
    decalPoint,
    decalNormal,
    stickerConfig,
    modelConfig,
  );
  const paintableTexture = usePaintableTexture(
    textureUvPoint,
    stickerConfig,
    modelConfig!,
  );
  const { focusToPoint, resetCamera, setControlsRef, defaultFocusDistance } =
    useCameraFocus(600, selectedModel);
  const [cursorStyle, setCursorStyle] = useState<string>("default");
  const lastMouseEventRef = useRef<ThreeEvent<PointerEvent> | null>(null);

  // 计算贴纸在3D世界中的尺寸
  const stickerSize3D = modelConfig
    ? calculateStickerSize3D(stickerConfig, modelConfig)
    : null;

  // Texture模式的拖拽功能
  const textureDrag = useStickerTextureDrag({
    stickerConfig,
    modelConfig,
    currentUvPoint: textureUvPoint,
    onUvPointUpdate: onTextureUvUpdate,
    meshRef,
  });

  /**
   * 同步拖拽状态到光标样式
   *
   * 监听拖拽状态的变化（如全局事件触发的状态更新），
   * 自动同步更新光标样式，确保视觉反馈的一致性。
   */
  useEffect(() => {
    if (interactionMode === "sticker" && mode === "texture") {
      const newCursorStyle = textureDrag.getCursorStyle();
      setCursorStyle(newCursorStyle);
    }
  }, [
    textureDrag.dragState.isDragging,
    textureDrag.dragState.isHovering,
    interactionMode,
    mode,
  ]);

  // === 3D 模型交互事件处理器 ===

  /**
   * 处理 3D 模型上的鼠标移动事件
   *
   * 职责：
   * 1. 将事件转发给拖拽处理器
   * 2. 记录最后的鼠标事件用于后续的状态恢复
   * 3. 立即更新光标样式以提供实时反馈
   *
   * @param event - Three.js 鼠标移动事件，包含 UV 坐标等信息
   */
  const handleModelPointerMove = (event: ThreeEvent<PointerEvent>) => {
    if (interactionMode === "sticker" && mode === "texture") {
      // 保存最后的鼠标事件，用于 pointerUp 时的状态恢复
      lastMouseEventRef.current = event;

      const moveResult = textureDrag.handleMouseMove(event);
      if (moveResult) {
        // 立即更新光标样式，提供实时的视觉反馈
        const newCursorStyle = textureDrag.getCursorStyle();
        setCursorStyle(newCursorStyle);
      }
    }
  };

  /**
   * 处理 3D 模型上的鼠标按下事件
   *
   * 简单转发事件到拖拽处理器，开始拖拽操作。
   * 光标样式会通过状态监听自动更新。
   *
   * @param event - Three.js 鼠标按下事件
   */
  const handleModelPointerDown = (event: ThreeEvent<PointerEvent>) => {
    if (interactionMode === "sticker" && mode === "texture") {
      textureDrag.handleMouseDown(event);
    }
  };

  /**
   * 处理 3D 模型上的鼠标释放事件
   *
   * 职责：
   * 1. 传递最后记录的鼠标事件给拖拽处理器以准确判断最终状态
   * 2. 使用返回的状态信息立即更新光标样式
   *
   * 这种设计确保了在拖拽结束时光标样式能够立即正确更新，
   * 而不需要等待下一次 useEffect 触发。
   */
  const handleModelPointerUp = () => {
    if (interactionMode === "sticker" && mode === "texture") {
      // 传递最后的鼠标事件以准确判断最终的 hover 状态
      const upResult = textureDrag.handleMouseUp(
        lastMouseEventRef.current || undefined,
      );
      if (upResult) {
        // 立即更新光标样式，避免视觉延迟
        const newCursorStyle = textureDrag.getCursorStyle({
          isDragging: upResult.isDragging,
          isHovering: upResult.isHovering,
        });
        setCursorStyle(newCursorStyle);
      }
    }
  };

  /**
   * 处理鼠标离开 3D 模型区域事件
   *
   * 职责：
   * 1. 将离开事件转发给拖拽处理器
   * 2. 根据返回的状态信息更新光标样式
   *
   * 在拖拽过程中离开模型区域时，会保持拖拽状态但清除 hover 状态，
   * 光标样式会相应地从 grab 变为 grabbing。
   */
  const handleModelPointerLeave = () => {
    if (interactionMode === "sticker" && mode === "texture") {
      const leaveResult = textureDrag.handleMouseLeave();

      // 使用返回的状态信息更新光标样式
      const styleOverride = {
        isHovering: leaveResult.isHovering,
        isDragging: leaveResult.isDragging,
      };
      const newCursorStyle = textureDrag.getCursorStyle(styleOverride);
      setCursorStyle(newCursorStyle);
    }
  };

  // 处理模型点击事件
  const handleModelClick = (event: ThreeEvent<MouseEvent>) => {
    if (interactionMode === "focus") {
      // 聚焦模式：点击mesh则聚焦
      if (event.object instanceof Mesh) {
        focusToPoint(event.point, defaultFocusDistance);
      }
    } else if (mode === "texture") {
      // Texture模式：如果不是在拖拽贴纸，则使用原有的放置逻辑
      if (!textureDrag.dragState.isDragging) {
        onMeshClick(event);
      }
    } else {
      // Decal模式：使用原有的贴图逻辑
      onMeshClick(event);
    }
  };

  // 安全地应用纹理到材质
  useEffect(() => {
    if (mode !== "texture" || !paintableTexture || !meshRef.current) return;

    const mesh = meshRef.current;
    const material = mesh.material;

    if (!material) {
      console.warn("Mesh has no material");
      return;
    }

    // 检查是否是标准材质且支持纹理贴图
    if (material instanceof MeshStandardMaterial) {
      if (material.map !== paintableTexture) {
        material.map = paintableTexture;
        material.needsUpdate = true;
      }
    } else {
      console.warn(
        "Material is not MeshStandardMaterial, texture may not apply correctly",
      );
    }
  }, [mode, paintableTexture, meshRef.current]);

  // 将resetCamera函数传递给父组件
  useEffect(() => {
    if (onCameraReset) {
      onCameraReset.current = resetCamera;
    }
  }, [resetCamera, onCameraReset]);

  return (
    <>
      <ambientLight intensity={0.6} />
      <directionalLight position={[2, 2, 2]} intensity={1} />

      <Stage
        key={`${selectedModel}-stage`}
        environment="city"
        intensity={0.6}
        adjustCamera={false}
      >
        <Model
          url={
            getModelConfig(selectedModel)?.modelPath ||
            `/assets/models/${selectedModel}.glb`
          }
          onMeshClick={handleModelClick}
          onPointerMove={handleModelPointerMove}
          onPointerDown={handleModelPointerDown}
          onPointerUp={handleModelPointerUp}
          onPointerLeave={handleModelPointerLeave}
          cursorStyle={cursorStyle}
        />

        {mode === "decal" &&
          decal &&
          meshRef.current &&
          stickerSize3D &&
          createPortal(
            <Sticker
              position={decal.position}
              rotation={decal.rotation}
              stickerConfig={decal.config}
              depth={decal.depth}
              size={
                new Vector3(
                  stickerSize3D.width,
                  stickerSize3D.height,
                  decal.depth,
                )
              }
            />,
            meshRef.current,
          )}
      </Stage>

      <OrbitControls
        ref={setControlsRef}
        key={`${selectedModel}-controls`}
        makeDefault
        enablePan={false}
        enableDamping={true}
        enableZoom={interactionMode === "sticker"} // 聚焦模式时禁用手动缩放
        enabled={!textureDrag.dragState.isDragging}
        minDistance={1}
        maxDistance={20}
        minPolarAngle={0}
        maxPolarAngle={Math.PI}
        rotateSpeed={0.5}
      />
    </>
  );
};
